import { createEntityAdapter } from '@reduxjs/toolkit';
import { ZodError } from 'zod';

import { type Attachment, isReceiptAttachment } from 'app-components/flux/transactions/transactionsTypes';
import { getLogger, getQcsDeletedResourceId } from 'app-components/utils/core';
import { createAppSlice } from 'app-components/utils/redux-store/createAppSlice';
import type RootState from 'app-components/utils/redux-store/rootState';

import { createReceiptCenterQueries, invalidateReceiptCenterQueries } from './receiptCenterQueries';
import { getReceiptSessionEpoch } from './receiptSessionEpoch';
import { getHttpErrorStatus, receiptsApi } from './receiptsApi';
import {
  isAmbiguousReceiptRequestError,
  type ReceiptRequestErrorPayload,
  toReceiptRequestErrorPayload,
} from './receiptsErrors';
import { isInFlightReceiptStatus, isLocalReceipt, isProcessingOrErrorReceipt } from './receiptsPredicates';
import {
  type AddPendingReceiptPayload,
  type ExpireStalePendingReceiptsPayload,
  type FetchReceiptsResponse,
  type LinkReceiptToTransactionPayload,
  type MarkReceiptCreationFailedPayload,
  type MarkReceiptCreationPendingPayload,
  type MarkReceiptUploadStartedPayload,
  RECEIPTS_REDUCER_KEY,
  type Receipt,
  type ReceiptFeedback,
  ReceiptFeedbackRating,
  type ReceiptPageParams,
  type ReceiptsState,
  type SubmitReceiptFeedbackPayload,
  type UpdateReceiptPayload,
} from './receiptsTypes';
import { linkReceiptWithRecovery } from './receiptTargetedLink';

const getTimeValue = (date: string): number => {
  const parsedDate = Date.parse(date);
  return Number.isNaN(parsedDate) ? 0 : parsedDate;
};

// UPLOAD_PENDING is entirely client-owned: if the browser has not finished putting the file into
// storage within three minutes, show an upload failure while retaining the local file for retry.
const RECEIPT_UPLOAD_TIMEOUT_MS = 3 * 60 * 1000;

// Receipt create/retry requests can time out at the gateway after the service accepts them. Keep
// reconciling those SCANNING rows for the service's 15-minute processing window before declaring
// a local creation or extraction failure.
const RECEIPT_RECONCILIATION_TIMEOUT_MS = 15 * 60 * 1000;

const AMBIGUOUS_RECEIPT_RETRY_ERROR_CODE = 'AMBIGUOUS_RECEIPT_RETRY';

class AmbiguousReceiptRetryError extends Error {
  readonly code = AMBIGUOUS_RECEIPT_RETRY_ERROR_CODE;

  constructor(error: unknown) {
    super(error instanceof Error ? error.message : 'Receipt retry result is uncertain.');
    this.name = 'AmbiguousReceiptRetryError';
  }
}

type DeleteReceiptResult = {
  deletedId: string;
  requestedId: string;
};

const getSubmittedFeedback = (submission: SubmitReceiptFeedbackPayload, updatedAt: string): ReceiptFeedback => {
  const submittedReasons =
    submission.rating === ReceiptFeedbackRating.NEGATIVE ? [...new Set(submission.reasons ?? [])] : [];
  const submittedComment =
    submission.rating === ReceiptFeedbackRating.NEGATIVE ? (submission.comment?.trim() ?? null) : null;

  return {
    rating: submission.rating,
    reasons: submittedReasons,
    comment: submittedComment,
    updatedAt,
  };
};

const feedbackMatchesSubmission = (
  feedback: ReceiptFeedback | null | undefined,
  submission: SubmitReceiptFeedbackPayload,
): boolean => {
  if (!feedback) return false;
  const submittedFeedback = getSubmittedFeedback(submission, feedback.updatedAt);

  return (
    feedback.rating === submittedFeedback.rating &&
    feedback.reasons.length === submittedFeedback.reasons.length &&
    feedback.reasons.every((reason, index) => reason === submittedFeedback.reasons[index]) &&
    feedback.comment === submittedFeedback.comment
  );
};

const getLatestModifiedDate = (currentDate: string | null, receipts: ReadonlyArray<Receipt>): string | null => {
  return receipts.reduce<string | null>((latestDate, receipt) => {
    if (!latestDate) {
      return receipt.modifiedDate;
    }

    return getTimeValue(receipt.modifiedDate) > getTimeValue(latestDate) ? receipt.modifiedDate : latestDate;
  }, currentDate);
};

const getLatestSyncDate = (
  currentDate: string | null,
  receipts: ReadonlyArray<Receipt>,
  responseAsOf?: string,
): string | null => {
  const latestModifiedDate = getLatestModifiedDate(currentDate, receipts);
  if (!responseAsOf) {
    return latestModifiedDate;
  }

  return !latestModifiedDate || getTimeValue(responseAsOf) > getTimeValue(latestModifiedDate)
    ? responseAsOf
    : latestModifiedDate;
};

const buildPendingReceipt = ({
  id,
  fileUri,
  fileName,
  mimeType,
  createdAt,
  localUploadOrder,
  isUploadQueued,
  targetTransactionId,
}: AddPendingReceiptPayload): Receipt => {
  const createdDate = new Date(createdAt).toISOString();

  return {
    id,
    documentId: id,
    name: fileName,
    mimeType,
    localFileUri: fileUri,
    localUploadOrder,
    isUploadQueued,
    targetTransactionId,
    status: 'UPLOAD_PENDING',
    createdDate,
    modifiedDate: createdDate,
  };
};

const receiptsAdapter = createEntityAdapter<Receipt, string>({
  selectId: (receipt) => receipt.id,
});

const selectReceiptsFromState = receiptsAdapter.getSelectors().selectAll;

const isInFlightReceipt = (receipt: Receipt): boolean => isInFlightReceiptStatus(receipt.status);

const isStaleLocalInFlightReceipt = (receipt: Receipt, now: number, activeUploadIds: ReadonlySet<string>): boolean =>
  (isLocalReceipt(receipt) || receipt.needsReconciliation === true) &&
  receipt.isUploadQueued !== true &&
  !activeUploadIds.has(receipt.id) &&
  isInFlightReceipt(receipt) &&
  now - getTimeValue(receipt.modifiedDate) >=
    (receipt.status === 'UPLOAD_PENDING' ? RECEIPT_UPLOAD_TIMEOUT_MS : RECEIPT_RECONCILIATION_TIMEOUT_MS);

// After a 409, a create still in flight may not be visible yet, so look up with bounded backoff.
const RECEIPT_REGISTRATION_LOOKUP_DELAYS_MS = [0, 250, 500, 1_000, 2_000] as const;

/** Looks up a registered receipt by document id; `answered` is false if any lookup request failed. */
const findRegisteredReceipt = async (documentId: string): Promise<{ receipt?: Receipt; answered: boolean }> => {
  let answered = true;
  for (const delayMs of RECEIPT_REGISTRATION_LOOKUP_DELAYS_MS) {
    if (delayMs > 0) await new Promise((resolve) => setTimeout(resolve, delayMs));
    try {
      const receipt = await receiptsApi.fetchByDocumentId(documentId);
      if (receipt?.documentId === documentId) return { receipt, answered };
    } catch {
      answered = false;
    }
  }
  return { answered };
};

const getLocalReceiptIdForCreatedReceipt = (
  currentReceipts: ReadonlyArray<Receipt>,
  createdReceipt: Receipt,
): string | undefined =>
  currentReceipts.find(
    (receipt) =>
      isLocalReceipt(receipt) && receipt.id !== createdReceipt.id && receipt.documentId === createdReceipt.documentId,
  )?.id;

const removeLocalReceiptsMatchingIncomingReceipts = (
  state: ReceiptsState,
  incomingReceipts: ReadonlyArray<Receipt>,
): void => {
  const currentReceipts = selectReceiptsFromState(state);
  const localReceiptIds = incomingReceipts
    .map((receipt) => getLocalReceiptIdForCreatedReceipt(currentReceipts, receipt))
    .filter((id): id is string => id !== undefined);

  receiptsAdapter.removeMany(state, localReceiptIds);
};

const getResourceVersion = (receipt: Receipt): number | undefined => {
  const version: unknown = 'dbVersion' in receipt ? receipt.dbVersion : undefined;
  return typeof version === 'number' ? version : undefined;
};

const isIncomingReceiptCurrent = (existingReceipt: Receipt, incomingReceipt: Receipt): boolean => {
  const existingVersion = getResourceVersion(existingReceipt);
  const incomingVersion = getResourceVersion(incomingReceipt);

  if (existingVersion !== undefined && incomingVersion !== undefined && existingVersion !== incomingVersion) {
    return incomingVersion > existingVersion;
  }

  return getTimeValue(incomingReceipt.modifiedDate) >= getTimeValue(existingReceipt.modifiedDate);
};

const preserveLoadedFeedback = (existingReceipt: Receipt | undefined, incomingReceipt: Receipt): Receipt => {
  if (
    existingReceipt &&
    Object.prototype.hasOwnProperty.call(existingReceipt, 'feedback') &&
    !Object.prototype.hasOwnProperty.call(incomingReceipt, 'feedback')
  ) {
    return { ...incomingReceipt, feedback: existingReceipt.feedback };
  }

  return incomingReceipt;
};

const preserveLocalUploadOrder = (existingReceipt: Receipt | undefined, incomingReceipt: Receipt): Receipt => {
  const receiptWithFeedback = preserveLoadedFeedback(existingReceipt, incomingReceipt);
  const receipt = existingReceipt?.targetTransactionId
    ? {
        ...receiptWithFeedback,
        targetTransactionId: existingReceipt.targetTransactionId,
        linkFailed: existingReceipt.linkFailed,
        isLinking: existingReceipt.isLinking,
        linkQueued: existingReceipt.linkQueued,
      }
    : receiptWithFeedback;
  if (
    !existingReceipt?.localUploadOrder ||
    (!isProcessingOrErrorReceipt(incomingReceipt) && incomingReceipt.status !== 'READY')
  ) {
    return receipt;
  }

  return { ...receipt, localUploadOrder: existingReceipt.localUploadOrder };
};

const getLocalReceiptsByDocumentId = (receipts: ReadonlyArray<Receipt>): Map<string, Receipt> =>
  new Map(receipts.filter(isLocalReceipt).map((receipt) => [receipt.documentId, receipt]));

const mergeCurrentReceipts = (state: ReceiptsState, incomingReceipts: ReadonlyArray<Receipt>): void => {
  const localReceiptsByDocumentId = getLocalReceiptsByDocumentId(selectReceiptsFromState(state));
  if (state.center?.enabled) {
    const registeredUploads = incomingReceipts.filter(
      (receipt) => !isLocalReceipt(receipt) && localReceiptsByDocumentId.has(receipt.documentId),
    );
    if (registeredUploads.length) {
      state.center.summaryPendingReceiptIds = [
        ...new Set([
          ...(state.center.summaryPendingReceiptIds ?? []),
          ...registeredUploads.map((receipt) => receipt.id),
        ]),
      ];
      // A summary started before registration cannot acknowledge the promoted upload.
      state.center.summaryRequestId = undefined;
      state.center.summaryRequestReceiptIds = undefined;
      state.center.revision += 1;
    }
  }
  removeLocalReceiptsMatchingIncomingReceipts(state, incomingReceipts);
  const currentReceipts = selectReceiptsFromState(state);
  const currentById = new Map(currentReceipts.map((receipt) => [receipt.id, receipt]));
  const receiptsToMerge = incomingReceipts.filter((receipt) => {
    const existingReceipt = currentById.get(receipt.id);
    return !existingReceipt || isIncomingReceiptCurrent(existingReceipt, receipt);
  });

  receiptsAdapter.setMany(
    state,
    receiptsToMerge.map((receipt) =>
      preserveLocalUploadOrder(
        currentById.get(receipt.id) ?? localReceiptsByDocumentId.get(receipt.documentId),
        receipt,
      ),
    ),
  );
};

const mergeReceiptDetail = (state: ReceiptsState, incomingReceipt: Receipt): void => {
  const existingReceipt = state.entities[incomingReceipt.id];
  if (!existingReceipt || isIncomingReceiptCurrent(existingReceipt, incomingReceipt)) {
    mergeCurrentReceipts(state, [incomingReceipt]);
    return;
  }

  if (Object.prototype.hasOwnProperty.call(incomingReceipt, 'feedback')) {
    receiptsAdapter.updateOne(state, {
      id: incomingReceipt.id,
      changes: { feedback: incomingReceipt.feedback },
    });
  }
};

const replaceWithFullReceiptSnapshot = (
  state: ReceiptsState,
  incomingReceipts: ReadonlyArray<Receipt>,
  responseAsOf?: string,
): void => {
  const localReceiptsByDocumentId = getLocalReceiptsByDocumentId(selectReceiptsFromState(state));
  removeLocalReceiptsMatchingIncomingReceipts(state, incomingReceipts);
  const currentReceipts = selectReceiptsFromState(state);
  const currentById = new Map(currentReceipts.map((receipt) => [receipt.id, receipt]));
  const incomingIds = new Set(incomingReceipts.map((receipt) => receipt.id));
  const snapshotTime = responseAsOf ? getTimeValue(responseAsOf) : 0;
  const currentIncomingReceipts = incomingReceipts.map((receipt) => {
    const existingReceipt = currentById.get(receipt.id);
    return existingReceipt && !isIncomingReceiptCurrent(existingReceipt, receipt)
      ? existingReceipt
      : preserveLocalUploadOrder(existingReceipt ?? localReceiptsByDocumentId.get(receipt.documentId), receipt);
  });
  const receiptsToPreserve = currentReceipts.filter(
    (receipt) =>
      !incomingIds.has(receipt.id) &&
      (isLocalReceipt(receipt) ||
        isInFlightReceipt(receipt) ||
        (snapshotTime > 0 && getTimeValue(receipt.modifiedDate) > snapshotTime)),
  );

  receiptsAdapter.setAll(state, [...currentIncomingReceipts, ...receiptsToPreserve]);
};

const recordLocalLinkChange = (state: ReceiptsState, receiptId: string, transactionId: string | null): void => {
  state.localLinkVersion += 1;
  const documentId = state.entities[receiptId]?.documentId ?? state.localLinkChanges[receiptId]?.documentId;
  state.localLinkChanges[receiptId] = { transactionId, version: state.localLinkVersion, documentId };
};

/** A settled change is new again: an editor opened while it was in flight still takes the result as local. */
const settleLocalLinkChange = (state: ReceiptsState, receiptId: string): void => {
  const change = state.localLinkChanges[receiptId];
  if (change) recordLocalLinkChange(state, receiptId, change.transactionId);
};

const dropLocalLinkChange = (state: ReceiptsState, receiptId: string): void => {
  delete state.localLinkChanges[receiptId];
};

const initialState: ReceiptsState = receiptsAdapter.getInitialState({
  error: null,
  isCreating: false,
  isLoading: false,
  sessionEpoch: 0,
  lastSyncDate: null,
  loadPending: true,
  totalElements: 0,
  totalPages: 0,
  center: createReceiptCenterQueries(),
  localLinkChanges: {},
  localLinkVersion: 0,
});

const log = getLogger('app-components/flux/receipts/receiptsSlice.ts');

const receiptsSlice = createAppSlice({
  name: RECEIPTS_REDUCER_KEY,
  initialState,
  reducers: (create) => ({
    setReceiptCenterQueryMode: create.reducer<boolean>((state, action) => {
      state.center ??= createReceiptCenterQueries();
      state.center.enabled = action.payload;
    }),
    setActiveReceiptReviewState: create.reducer<'NEEDS_REVIEW' | 'REVIEWED'>((state, action) => {
      state.center ??= createReceiptCenterQueries();
      state.center.enabled = true;
      state.center.activeReviewState = action.payload;
    }),
    setReceiptPageQuery: create.reducer<ReceiptPageParams>((state, action) => {
      state.center ??= createReceiptCenterQueries();
      const query = state.center.pages[action.payload.reviewState];
      Object.assign(query, {
        page: action.payload.page,
        pageSize: action.payload.pageSize,
        sort: action.payload.sort,
        ids: [],
        loaded: false,
        requestId: undefined,
        isLoading: false,
        revision: query.revision + 1,
      });
    }),
    invalidateReceiptCenter: create.reducer<boolean>((state, action) =>
      invalidateReceiptCenterQueries(state, action.payload === true),
    ),
    fetchReceiptSummary: create.asyncThunk(async (_arg: void) => receiptsApi.fetchSummary(), {
      pending(state, action) {
        state.center ??= createReceiptCenterQueries();
        state.center.summaryRequestId = action.meta.requestId;
        state.center.summaryRequestReceiptIds = state.center.summaryPendingReceiptIds?.slice() ?? [];
        state.center.summaryError = null;
      },
      fulfilled(state, action) {
        if (state.center?.summaryRequestId !== action.meta.requestId) return;
        state.center.summary = action.payload;
        const acknowledgedIds = new Set(state.center.summaryRequestReceiptIds);
        state.center.summaryPendingReceiptIds = state.center.summaryPendingReceiptIds?.filter(
          (id) => !acknowledgedIds.has(id),
        );
        state.center.summaryRequestReceiptIds = undefined;
        state.center.summaryRequestId = undefined;
      },
      rejected(state, action) {
        if (state.center?.summaryRequestId !== action.meta.requestId) return;
        state.center.summaryError = action.error;
        state.center.summaryRequestId = undefined;
      },
    }),
    fetchReceiptPage: create.asyncThunk(
      async (params: ReceiptPageParams): Promise<FetchReceiptsResponse> => receiptsApi.fetchPage(params),
      {
        pending(state, action) {
          state.center ??= createReceiptCenterQueries();
          const query = state.center.pages[action.meta.arg.reviewState];
          query.requestId = action.meta.requestId;
          query.isLoading = true;
          query.error = null;
        },
        fulfilled(state, action) {
          const query = state.center?.pages[action.meta.arg.reviewState];
          if (!query || query.requestId !== action.meta.requestId) return;
          mergeCurrentReceipts(state, action.payload.resources);
          query.ids = [
            ...new Set(action.payload.resources.filter((receipt) => !receipt.isDeleted).map((receipt) => receipt.id)),
          ];
          query.total = action.payload.totalElements;
          query.loaded = true;
          query.isLoading = false;
          query.requestId = undefined;
          const lastPage = Math.max(0, Math.ceil(query.total / query.pageSize) - 1);
          if (query.page > lastPage) {
            query.page = lastPage;
            query.revision += 1;
          }
        },
        rejected(state, action) {
          const query = state.center?.pages[action.meta.arg.reviewState];
          if (!query || query.requestId !== action.meta.requestId) return;
          query.isLoading = false;
          query.requestId = undefined;
          query.error = action.error;
        },
      },
    ),
    reconcileReceiptCreation: create.asyncThunk(
      async (documentId: string) => receiptsApi.fetchByDocumentId(documentId),
      {
        fulfilled(state, action) {
          if (action.payload) mergeCurrentReceipts(state, [action.payload]);
        },
      },
    ),
    fetchReceiptByDocumentId: create.asyncThunk(
      async (documentId: string): Promise<Receipt | undefined> => receiptsApi.fetchByDocumentId(documentId),
      {
        fulfilled(state, action) {
          if (action.payload) {
            mergeCurrentReceipts(state, [action.payload]);
          } else {
            // Session entries can outlive a receipt deleted while the page was closed.
            receiptsAdapter.removeMany(
              state,
              selectReceiptsFromState(state)
                .filter((receipt) => receipt.documentId === action.meta.arg && !isLocalReceipt(receipt))
                .map((receipt) => receipt.id),
            );
          }
        },
      },
    ),
    createReceipt: create.asyncThunk.withTypes<{ rejectValue: ReceiptRequestErrorPayload }>()(
      async (payload: Parameters<typeof receiptsApi.create>[0], thunkApi) => {
        try {
          return await receiptsApi.create(payload);
        } catch (error) {
          const errorPayload = toReceiptRequestErrorPayload(error);
          // Registering a document again returns 409. For a transaction-targeted receipt, recover the
          // existing receipt by document id so a retried registration does not fail at the same step.
          const targeted = selectReceiptsFromState((thunkApi.getState() as RootState)[RECEIPTS_REDUCER_KEY]).some(
            (receipt) => receipt.documentId === payload.documentId && Boolean(receipt.targetTransactionId),
          );
          if (!targeted || errorPayload.status !== 409) return thunkApi.rejectWithValue(errorPayload);

          const storeEpoch = getReceiptSessionEpoch(thunkApi.dispatch);
          const lookup = await findRegisteredReceipt(payload.documentId);
          if (getReceiptSessionEpoch(thunkApi.dispatch) !== storeEpoch) return thunkApi.rejectWithValue(errorPayload);
          if (lookup.receipt) return lookup.receipt;
          // Every lookup answered and none found the receipt: it was deleted, and its document id stays
          // reserved. A failed lookup leaves the create retryable.
          return thunkApi.rejectWithValue(lookup.answered ? { ...errorPayload, documentReserved: true } : errorPayload);
        }
      },
      {
        pending(state) {
          state.isCreating = true;
          state.error = null;
        },
        fulfilled(state, action) {
          mergeCurrentReceipts(state, [action.payload]);
          state.lastSyncDate = getLatestModifiedDate(state.lastSyncDate, [action.payload]);
        },
        rejected(state, action) {
          log.error('Failed to create receipt', action.error);
          state.error = action.error;
          if (!action.payload?.documentReserved) return;
          // Retrying cannot register a reserved document, so leave only Delete, as for other permanent errors.
          receiptsAdapter.updateMany(
            state,
            selectReceiptsFromState(state)
              .filter(
                (receipt) =>
                  isLocalReceipt(receipt) &&
                  receipt.documentId === action.meta.arg.documentId &&
                  Boolean(receipt.targetTransactionId),
              )
              .map((receipt) => ({
                id: receipt.id,
                changes: {
                  localFileUri: undefined,
                  needsReconciliation: false,
                  status: 'UNPROCESSABLE' as const,
                  subStatus: 'CREATION_FAILED' as const,
                  modifiedDate: new Date().toISOString(),
                },
              })),
          );
        },
        settled(state) {
          state.isCreating = false;
        },
      },
    ),
    fetchReceipts: create.asyncThunk(
      async (params: Parameters<typeof receiptsApi.fetch>[0]): Promise<FetchReceiptsResponse> =>
        receiptsApi.fetchAll(params),
      {
        pending(state) {
          state.isLoading = true;
          state.error = null;
        },
        fulfilled(state, action) {
          if (action.meta.arg?.modifiedAfter || (action.meta.arg?.page ?? 1) > 1) {
            mergeCurrentReceipts(state, action.payload.resources);
          } else {
            replaceWithFullReceiptSnapshot(state, action.payload.resources, action.payload.metaData?.asOf);
          }

          state.totalElements = action.payload.totalElements;
          state.totalPages = action.payload.totalPages;
          state.lastSyncDate = getLatestSyncDate(
            state.lastSyncDate,
            action.payload.resources,
            action.payload.metaData?.asOf,
          );
          state.loadPending = false;
        },
        rejected(state, action) {
          log.error('Failed to fetch receipts', action.error);
          state.error = action.error;
        },
        settled(state) {
          state.isLoading = false;
        },
      },
    ),
    fetchReceiptsForAttachments: create.asyncThunk(
      async (attachments: ReadonlyArray<Attachment>, thunkApi): Promise<Receipt[]> => {
        const receipts = await Promise.all(
          attachments.map(async (attachment): Promise<Receipt | undefined> => {
            try {
              if (!isReceiptAttachment(attachment)) {
                const receipt = await receiptsApi.fetchByDocumentId(attachment.id);
                if (receipt) return receipt;
                // A transaction write can strip RECEIPT from an attachment carrying the receipt id,
                // rather than the document id. Ordinary storage attachments have no receipt detail.
              }
              const currentReceipt = (thunkApi.getState() as RootState)[RECEIPTS_REDUCER_KEY].entities[attachment.id];
              return await receiptsApi.findById(attachment.id, currentReceipt?.documentId);
            } catch (error) {
              if (getHttpErrorStatus(error) === 404) return undefined;
              // QCS answers a receipt deleted elsewhere with a 400. Drop the stale copy so the
              // transaction it stays attached to stops showing it and can take a new receipt.
              if (getQcsDeletedResourceId(error) === attachment.id) {
                thunkApi.dispatch(receiptsActions.removeReceipt(attachment.id));
                return undefined;
              }
              throw error;
            }
          }),
        );
        return receipts.filter((receipt): receipt is Receipt => receipt !== undefined);
      },
      {
        fulfilled(state, action) {
          action.payload.forEach((receipt) => mergeReceiptDetail(state, receipt));
        },
      },
    ),
    fetchReceiptDetail: create.asyncThunk(
      async (id: string, thunkApi): Promise<Receipt> => {
        const currentReceipt = (thunkApi.getState() as RootState)[RECEIPTS_REDUCER_KEY].entities[id];
        try {
          return await receiptsApi.fetchById(id, currentReceipt?.documentId);
        } catch (error) {
          const paginated = (thunkApi.getState() as RootState)[RECEIPTS_REDUCER_KEY].center?.enabled;
          // QCS confirms a deletion by id in either client; a bare 404 removes it only when paginated.
          if (getQcsDeletedResourceId(error) === id || (paginated && getHttpErrorStatus(error) === 404)) {
            thunkApi.dispatch(receiptsActions.removeReceipt(id));
          }
          throw error;
        }
      },
      {
        fulfilled(state, action) {
          mergeReceiptDetail(state, action.payload);
        },
        rejected(_state, action) {
          log.error('Failed to fetch receipt detail', action.error);
        },
      },
    ),
    deleteReceipt: create.asyncThunk(
      async (id: string, thunkApi): Promise<DeleteReceiptResult> => {
        const receipt = (thunkApi.getState() as RootState)[RECEIPTS_REDUCER_KEY].entities[id];
        let serviceReceiptId = id;

        if (receipt && isLocalReceipt(receipt) && receipt.needsReconciliation === true) {
          const paginated = (thunkApi.getState() as RootState)[RECEIPTS_REDUCER_KEY].center?.enabled;
          const response = paginated
            ? {
                resources: [await receiptsApi.fetchByDocumentId(receipt.documentId)].filter(
                  (candidate): candidate is Receipt => candidate !== undefined,
                ),
              }
            : await receiptsApi.fetchAll();
          const reconciledReceipt = response.resources.find(
            (candidate) => !isLocalReceipt(candidate) && candidate.documentId === receipt.documentId,
          );

          if (!reconciledReceipt) {
            throw new Error('Receipt creation is still reconciling. Try deleting it again after the next sync.');
          }

          serviceReceiptId = reconciledReceipt.id;
        }

        await receiptsApi.delete(serviceReceiptId);
        return { deletedId: serviceReceiptId, requestedId: id };
      },
      {
        pending(state, action) {
          recordLocalLinkChange(state, action.meta.arg, null);
        },
        fulfilled(state, action) {
          settleLocalLinkChange(state, action.meta.arg);
          receiptsAdapter.removeMany(state, [action.payload.requestedId, action.payload.deletedId]);
        },
        rejected(state, action) {
          log.error('Failed to delete receipt', action.error);
          state.error = action.error;
          dropLocalLinkChange(state, action.meta.arg);
        },
      },
    ),
    retryReceipt: create.asyncThunk(
      async (id: string): Promise<Receipt> => {
        try {
          return await receiptsApi.retry(id);
        } catch (error) {
          // 409 means the receipt is no longer retryable (e.g. it became UNPROCESSABLE); refresh it
          // so the UI reflects the terminal state instead of surfacing an error.
          if (getHttpErrorStatus(error) === 409) {
            return receiptsApi.fetchById(id);
          }

          if (isAmbiguousReceiptRequestError(error)) {
            throw new AmbiguousReceiptRetryError(error);
          }

          throw error;
        }
      },
      {
        pending(state, action) {
          receiptsAdapter.updateOne(state, {
            id: action.meta.arg,
            changes: {
              status: 'SCANNING',
            },
          });
        },
        fulfilled(state, action) {
          mergeReceiptDetail(state, action.payload);
          receiptsAdapter.updateOne(state, { id: action.payload.id, changes: { feedback: undefined } });
          state.lastSyncDate = getLatestModifiedDate(state.lastSyncDate, [action.payload]);
        },
        rejected(state, action) {
          log.error('Failed to retry receipt processing', action.error);
          state.error = action.error;
          if (action.error.code !== AMBIGUOUS_RECEIPT_RETRY_ERROR_CODE) {
            receiptsAdapter.updateOne(state, {
              id: action.meta.arg,
              changes: {
                status: 'FAILED',
              },
            });
            return;
          }

          receiptsAdapter.updateOne(state, {
            id: action.meta.arg,
            changes: {
              needsReconciliation: true,
              status: 'SCANNING',
              subStatus: undefined,
              modifiedDate: new Date().toISOString(),
            },
          });
        },
      },
    ),
    markReceiptViewed: create.asyncThunk(async (id: string): Promise<Receipt> => receiptsApi.markViewed(id), {
      fulfilled(state, action) {
        mergeCurrentReceipts(state, [action.payload]);
        state.lastSyncDate = getLatestModifiedDate(state.lastSyncDate, [action.payload]);
      },
      rejected(state, action) {
        log.error('Failed to mark receipt viewed', action.error);
        state.error = action.error;
      },
    }),
    updateReceipt: create.asyncThunk(
      async (payload: UpdateReceiptPayload): Promise<Receipt> => receiptsApi.update(payload),
      {
        fulfilled(state, action) {
          mergeCurrentReceipts(state, [action.payload]);
          state.lastSyncDate = getLatestModifiedDate(state.lastSyncDate, [action.payload]);
        },
        rejected(state, action) {
          log.error('Failed to update receipt', action.error);
          state.error = action.error;
        },
      },
    ),
    submitReceiptFeedback: create.asyncThunk(
      async (payload: SubmitReceiptFeedbackPayload, thunkApi): Promise<Receipt> => {
        try {
          return await receiptsApi.submitFeedback(payload);
        } catch (error) {
          const responseParsingFailed = error instanceof ZodError;
          if (!responseParsingFailed && !isAmbiguousReceiptRequestError(error)) throw error;

          try {
            const currentReceipt = (thunkApi.getState() as RootState)[RECEIPTS_REDUCER_KEY].entities[payload.id];
            const detail = await receiptsApi.fetchById(payload.id, currentReceipt?.documentId);
            if (responseParsingFailed || feedbackMatchesSubmission(detail.feedback, payload)) return detail;
          } catch {
            // A legacy detail can fail the same modern receipt schema as the successful PUT response.
          }

          if (responseParsingFailed) {
            const currentReceipt = (thunkApi.getState() as RootState)[RECEIPTS_REDUCER_KEY].entities[payload.id];
            if (currentReceipt) {
              return {
                ...currentReceipt,
                feedback: getSubmittedFeedback(payload, new Date().toISOString()),
              };
            }
          }

          throw error;
        }
      },
      {
        fulfilled(state, action) {
          mergeReceiptDetail(state, action.payload);
        },
        rejected(_state, action) {
          log.error('Failed to submit receipt feedback', action.error);
        },
      },
    ),
    linkReceiptToTransaction: create.asyncThunk(
      async (payload: LinkReceiptToTransactionPayload): Promise<Receipt> => receiptsApi.link(payload),
      {
        pending(state, action) {
          recordLocalLinkChange(state, action.meta.arg.id, action.meta.arg.transactionId);
        },
        fulfilled(state, action) {
          settleLocalLinkChange(state, action.meta.arg.id);
          mergeCurrentReceipts(state, [action.payload]);
          state.lastSyncDate = getLatestModifiedDate(state.lastSyncDate, [action.payload]);
        },
        rejected(state, action) {
          log.error('Failed to link receipt to transaction', action.error);
          state.error = action.error;
          dropLocalLinkChange(state, action.meta.arg.id);
        },
      },
    ),
    repairReceiptAttachment: create.asyncThunk(
      async (
        payload: LinkReceiptToTransactionPayload & { sessionEpoch: number; storeEpoch: number },
        thunkApi,
      ): Promise<Receipt> => {
        const isCurrentSession = (): boolean =>
          (thunkApi.getState() as RootState)[RECEIPTS_REDUCER_KEY].sessionEpoch === payload.sessionEpoch &&
          getReceiptSessionEpoch(thunkApi.dispatch) === payload.storeEpoch;
        if (!isCurrentSession()) throw new Error('Receipt repair belongs to a previous session.');
        // A detach elsewhere (another device or tab) leaves this store's copy stale. Only re-link while
        // the service still reports the link; otherwise return the detached receipt to refresh the store.
        const currentReceipt = await receiptsApi.fetchById(payload.id);
        if (!isCurrentSession()) throw new Error('Receipt repair belongs to a previous session.');
        if (currentReceipt.transactionId !== payload.transactionId) return currentReceipt;
        const linkedReceipt = await receiptsApi.link({ id: payload.id, transactionId: payload.transactionId });
        if (!isCurrentSession()) throw new Error('Receipt repair belongs to a previous session.');
        return linkedReceipt;
      },
      {
        fulfilled(state, action) {
          if (state.sessionEpoch !== action.meta.arg.sessionEpoch) return;
          if (state.entities[action.meta.arg.id]?.transactionId !== action.meta.arg.transactionId) return;
          mergeCurrentReceipts(state, [action.payload]);
          state.lastSyncDate = getLatestModifiedDate(state.lastSyncDate, [action.payload]);
        },
      },
    ),
    linkTargetedReceiptToTransaction: create.asyncThunk.withTypes<{ rejectValue: 'STALE_SESSION' }>()(
      async (payload: LinkReceiptToTransactionPayload, thunkApi) => {
        const sessionEpoch = (thunkApi.getState() as RootState)[RECEIPTS_REDUCER_KEY].sessionEpoch;
        const storeEpoch = getReceiptSessionEpoch(thunkApi.dispatch);
        const isCurrentSession = (): boolean =>
          (thunkApi.getState() as RootState)[RECEIPTS_REDUCER_KEY].sessionEpoch === sessionEpoch &&
          getReceiptSessionEpoch(thunkApi.dispatch) === storeEpoch;
        try {
          return await linkReceiptWithRecovery(payload, receiptsApi, isCurrentSession);
        } catch (error) {
          if (!isCurrentSession()) return thunkApi.rejectWithValue('STALE_SESSION');
          throw error;
        }
      },
      {
        options: {
          condition(payload, { getState }): boolean {
            const receipt = (getState() as RootState)[RECEIPTS_REDUCER_KEY].entities[payload.id];
            return (
              receipt?.status === 'READY' &&
              receipt.targetTransactionId === payload.transactionId &&
              receipt.isLinking !== true
            );
          },
        },
        pending(state, action) {
          recordLocalLinkChange(state, action.meta.arg.id, action.meta.arg.transactionId);
          const receipt = state.entities[action.meta.arg.id];
          if (receipt?.targetTransactionId === action.meta.arg.transactionId) {
            receiptsAdapter.updateOne(state, {
              id: receipt.id,
              changes: { isLinking: true, linkFailed: false, linkQueued: undefined },
            });
          }
        },
        fulfilled(state, action) {
          // A session boundary clears targets; a late response must not apply to the next session.
          if (state.entities[action.meta.arg.id]?.targetTransactionId !== action.meta.arg.transactionId) return;
          settleLocalLinkChange(state, action.meta.arg.id);
          mergeCurrentReceipts(state, [action.payload]);
          receiptsAdapter.updateOne(state, {
            id: action.payload.id,
            changes: {
              transactionId: action.payload.transactionId,
              targetTransactionId: undefined,
              linkFailed: undefined,
              isLinking: undefined,
              linkQueued: undefined,
            },
          });
          state.lastSyncDate = getLatestModifiedDate(state.lastSyncDate, [action.payload]);
        },
        rejected(state, action) {
          dropLocalLinkChange(state, action.meta.arg.id);
          if (action.payload === 'STALE_SESSION') return;
          const receipt = state.entities[action.meta.arg.id];
          if (receipt?.targetTransactionId === action.meta.arg.transactionId) {
            log.error('Failed to link targeted receipt to transaction', action.error);
            state.error = action.error;
            receiptsAdapter.updateOne(state, {
              id: receipt.id,
              changes: { isLinking: false, linkFailed: true, linkQueued: undefined },
            });
          }
        },
      },
    ),
    unlinkReceiptFromTransaction: create.asyncThunk(async (id: string): Promise<Receipt> => receiptsApi.unlink(id), {
      pending(state, action) {
        recordLocalLinkChange(state, action.meta.arg, null);
      },
      fulfilled(state, action) {
        settleLocalLinkChange(state, action.meta.arg);
        mergeCurrentReceipts(state, [action.payload]);
        state.lastSyncDate = getLatestModifiedDate(state.lastSyncDate, [action.payload]);
      },
      rejected(state, action) {
        log.error('Failed to unlink receipt from transaction', action.error);
        state.error = action.error;
        dropLocalLinkChange(state, action.meta.arg);
      },
    }),
    /** Returns an editor's unsaved receipt to Receipt Center. */
    clearReceiptTarget: create.reducer<string>((state, action) => {
      if (!state.entities[action.payload]?.targetTransactionId) return;
      receiptsAdapter.updateOne(state, {
        id: action.payload,
        changes: { targetTransactionId: undefined, linkFailed: undefined, isLinking: undefined, linkQueued: undefined },
      });
    }),
    setReceiptLinkQueued: create.reducer<{ id: string; queued: boolean }>((state, action) => {
      if (!state.entities[action.payload.id]?.targetTransactionId) return;
      receiptsAdapter.updateOne(state, {
        id: action.payload.id,
        changes: { linkQueued: action.payload.queued || undefined },
      });
    }),
    /** Drops client-owned transaction targets so no queued or late work links after a session boundary. */
    clearTransactionTargets: create.reducer((state) => {
      state.sessionEpoch += 1;
      state.localLinkChanges = {};
      receiptsAdapter.updateMany(
        state,
        Object.values(state.entities)
          .filter((receipt): receipt is Receipt => Boolean(receipt?.targetTransactionId))
          .map((receipt) => ({
            id: receipt.id,
            changes: {
              targetTransactionId: undefined,
              linkFailed: undefined,
              isLinking: undefined,
              linkQueued: undefined,
            },
          })),
      );
    }),
    addPendingReceipt: create.reducer<AddPendingReceiptPayload>((state, action) => {
      receiptsAdapter.upsertOne(state, buildPendingReceipt(action.payload));
    }),
    markReceiptUploadStarted: create.reducer<MarkReceiptUploadStartedPayload>((state, action) => {
      receiptsAdapter.updateOne(state, {
        id: action.payload.id,
        changes: {
          isUploadQueued: false,
          modifiedDate: new Date(action.payload.now).toISOString(),
        },
      });
    }),
    expireStalePendingReceipts: create.reducer<ExpireStalePendingReceiptsPayload>((state, action) => {
      const modifiedDate = new Date(action.payload.now).toISOString();
      const activeUploadIds = new Set(action.payload.activeUploadIds ?? []);
      const updates = selectReceiptsFromState(state)
        .filter((receipt) => isStaleLocalInFlightReceipt(receipt, action.payload.now, activeUploadIds))
        .map((receipt) => ({
          id: receipt.id,
          changes:
            receipt.status === 'UPLOAD_PENDING'
              ? {
                  status: 'FAILED' as const,
                  subStatus: 'UPLOAD_FAILED' as const,
                  modifiedDate,
                }
              : {
                  localFileUri: undefined,
                  needsReconciliation: false,
                  status: 'FAILED' as const,
                  subStatus: isLocalReceipt(receipt) ? ('CREATION_FAILED' as const) : ('EXTRACTION_FAILED' as const),
                  modifiedDate,
                },
        }));

      receiptsAdapter.updateMany(state, updates);
    }),
    markReceiptCreationPending: create.reducer<MarkReceiptCreationPendingPayload>((state, action) => {
      receiptsAdapter.updateOne(state, {
        id: action.payload.id,
        changes: {
          documentId: action.payload.documentId,
          name: action.payload.documentName,
          mimeType: action.payload.mimeType,
          localFileUri: undefined,
          needsReconciliation: false,
          status: 'SCANNING',
          subStatus: undefined,
          modifiedDate: new Date().toISOString(),
        },
      });
    }),
    markReceiptCreationUncertain: create.reducer<MarkReceiptCreationPendingPayload>((state, action) => {
      receiptsAdapter.updateOne(state, {
        id: action.payload.id,
        changes: {
          documentId: action.payload.documentId,
          name: action.payload.documentName,
          mimeType: action.payload.mimeType,
          localFileUri: undefined,
          needsReconciliation: true,
          status: 'SCANNING',
          subStatus: undefined,
          modifiedDate: new Date().toISOString(),
        },
      });
    }),
    markReceiptCreationFailed: create.reducer<MarkReceiptCreationFailedPayload>((state, action) => {
      // A reserved document was already marked permanent when its create was rejected.
      if (state.entities[action.payload.id]?.status === 'UNPROCESSABLE') return;
      receiptsAdapter.updateOne(state, {
        id: action.payload.id,
        changes: {
          documentId: action.payload.documentId,
          name: action.payload.documentName,
          mimeType: action.payload.mimeType,
          localFileUri: undefined,
          needsReconciliation: false,
          status: 'FAILED',
          subStatus: 'CREATION_FAILED',
          modifiedDate: new Date().toISOString(),
        },
      });
    }),
    markReceiptUploadFailed: create.reducer<string>((state, action) => {
      receiptsAdapter.updateOne(state, {
        id: action.payload,
        changes: {
          needsReconciliation: false,
          status: 'FAILED',
          subStatus: 'UPLOAD_FAILED',
          modifiedDate: new Date().toISOString(),
        },
      });
    }),
    mergeReceipts: create.reducer<Receipt[]>((state, action) => {
      mergeCurrentReceipts(state, action.payload);
      state.lastSyncDate = getLatestModifiedDate(state.lastSyncDate, action.payload);
    }),
    removeReceipt: create.reducer<string>((state, action) => {
      receiptsAdapter.removeOne(state, action.payload);
    }),
  }),
  extraReducers: (builder) => {
    builder.addMatcher(
      (action) =>
        [
          'createReceipt/fulfilled',
          'deleteReceipt/fulfilled',
          'retryReceipt/fulfilled',
          'updateReceipt/fulfilled',
          'linkReceiptToTransaction/fulfilled',
          'linkTargetedReceiptToTransaction/fulfilled',
          'repairReceiptAttachment/fulfilled',
          'unlinkReceiptFromTransaction/fulfilled',
          'reconcileReceiptCreation/fulfilled',
          'mergeReceipts',
          'removeReceipt',
        ].some((suffix) => action.type === `${RECEIPTS_REDUCER_KEY}/${suffix}`),
      (state) => invalidateReceiptCenterQueries(state),
    );
  },
  selectors: {
    ...receiptsAdapter.getSelectors(),
  },
});

export const receiptsActions = receiptsSlice.actions;
export { receiptsAdapter, receiptsSlice };
